
import React, { useState } from 'react';
import * as XLSX from 'xlsx';
import { 
  Equipment, 
  InspectionPlan, 
  InspectionActual, 
  Finding,
  InspectionStatus,
  FollowUpStatus 
} from '../types';
import { 
  FileDown, 
  FileSpreadsheet, 
  FileText, 
  Database,
  Search,
  CheckCircle2,
  Calendar,
  Download,
  AlertTriangle,
  AlertCircle,
  Clock,
  ArrowRight,
  Activity,
  ShieldAlert,
  Target,
  Users
} from 'lucide-react';

interface ReportsProps {
  equipments: Equipment[];
  plans: InspectionPlan[];
  actuals: InspectionActual[];
  findings: Finding[];
  goToInput?: (equipmentId: string) => void;
}

const Reports: React.FC<ReportsProps> = ({ equipments, plans, actuals, findings, goToInput }) => {
  const [customQuery, setCustomQuery] = useState('');
  const todayStr = new Date().toISOString().split('T')[0];

  // Logic for Today and Pending status
  const todayPlans = plans.filter(p => p.plannedDate === todayStr);
  const pendingPlans = plans.filter(p => {
    const isPast = p.plannedDate < todayStr;
    const hasActual = actuals.some(a => a.planId === p.id || (a.equipmentId === p.equipmentId && a.actualDate === p.plannedDate));
    return isPast && !hasActual;
  });

  const todayActuals = actuals.filter(a => a.actualDate === todayStr);
  
  const exportToExcel = (data: any[], fileName: string) => {
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Data");
    XLSX.writeFile(workbook, `${fileName}.xlsx`);
  };

  const handleDownloadMonitoring = () => {
    const data = plans.map(p => {
      const eq = equipments.find(e => e.id === p.equipmentId);
      const act = actuals.find(a => a.planId === p.id || (a.equipmentId === p.equipmentId && a.actualDate === p.plannedDate));
      
      return {
        'Kode Alat': eq?.code || 'N/A',
        'Jenis Alat': eq?.type || 'N/A',
        'Area': eq?.area || 'N/A',
        'Lokasi': eq?.location || 'N/A',
        'Tanggal Rencana': p.plannedDate,
        'PIC Rencana': p.pic,
        'Tanggal Aktual': act?.actualDate || '-',
        'Inspektur': act?.inspector || '-',
        'Status Hasil': act?.status || 'Belum Inspeksi',
        'Catatan': act?.notes || '-'
      };
    });
    exportToExcel(data, `Rekap_Monitoring_Safety_${new Date().getFullYear()}`);
  };

  const handleDownloadActuals = () => {
    const data = actuals.map(a => {
      const eq = equipments.find(e => e.id === a.equipmentId);
      return {
        'Tanggal Inspeksi': a.actualDate,
        'Kode Alat': eq?.code || 'N/A',
        'Jenis Alat': eq?.type || 'N/A',
        'Area': eq?.area || 'N/A',
        'Status': a.status,
        'Inspektur': a.inspector,
        'Catatan': a.notes || ''
      };
    });
    exportToExcel(data, `Logbook_Inspeksi_Harian_${new Date().toISOString().split('T')[0]}`);
  };

  const handleDownloadFindings = () => {
    const data = findings.map(f => {
      const act = actuals.find(a => a.id === f.inspectionId);
      const eq = act ? equipments.find(e => e.id === act.equipmentId) : null;
      return {
        'Deskripsi Temuan': f.description,
        'Alat Terkait': eq?.code || 'N/A',
        'Area': eq?.area || 'N/A',
        'Rekomendasi': f.actionRequired,
        'PIC Perbaikan': f.pic,
        'Target Selesai': f.targetDate,
        'Status': f.status
      };
    });
    exportToExcel(data, `Rekap_Temuan_NG_${new Date().toISOString().split('T')[0]}`);
  };

  const handleDownloadEquipments = () => {
    const data = equipments.map(e => ({
      'Kode Alat': e.code,
      'Jenis': e.type,
      'Area': e.area,
      'Lokasi Detail': e.location,
      'Frekuensi Inspeksi': e.frequency,
      'ID Sistem': e.id
    }));
    exportToExcel(data, 'Master_Data_Peralatan_Safety');
  };

  const handleDownloadCustom = () => {
    const query = customQuery.toLowerCase();
    const joinedData = plans.map(p => {
      const eq = equipments.find(e => e.id === p.equipmentId);
      const act = actuals.find(a => a.planId === p.id || (a.equipmentId === p.equipmentId && a.actualDate === p.plannedDate));
      
      return {
        code: eq?.code || '',
        type: eq?.type || '',
        area: eq?.area || '',
        location: eq?.location || '',
        plannedDate: p.plannedDate,
        pic: p.pic,
        actualDate: act?.actualDate || '',
        inspector: act?.inspector || '',
        status: act?.status || 'Belum Inspeksi',
        notes: act?.notes || ''
      };
    });

    const filteredData = joinedData.filter(row => 
      row.code.toLowerCase().includes(query) ||
      row.area.toLowerCase().includes(query) ||
      row.pic.toLowerCase().includes(query) ||
      row.inspector.toLowerCase().includes(query) ||
      row.type.toLowerCase().includes(query)
    ).map(row => ({
        'Kode Alat': row.code,
        'Jenis Alat': row.type,
        'Area': row.area,
        'Lokasi': row.location,
        'Tanggal Rencana': row.plannedDate,
        'PIC Rencana': row.pic,
        'Tanggal Aktual': row.actualDate || '-',
        'Inspektur': row.inspector || '-',
        'Status Hasil': row.status,
        'Catatan': row.notes || '-'
    }));

    const suffix = customQuery.trim() ? customQuery.replace(/\s+/g, '_') : 'Filtered';
    exportToExcel(filteredData, `Custom_Audit_Report_${suffix}_${new Date().toISOString().split('T')[0]}`);
  };

  const reportTypes = [
    { 
      title: 'Laporan Monitoring Tahunan', 
      desc: 'Rekap plan vs aktual periode aktif running year.', 
      icon: <Calendar className="w-6 h-6" />, 
      color: 'bg-emerald-500',
      onDownload: handleDownloadMonitoring
    },
    { 
      title: 'Logbook Inspeksi Harian', 
      desc: 'Riwayat pengecekan aktual semua aset safety.', 
      icon: <Database className="w-6 h-6" />, 
      color: 'bg-blue-500',
      onDownload: handleDownloadActuals
    },
    { 
      title: 'Rekap Temuan (NG)', 
      desc: 'Laporan tindak lanjut dan penyelesaian temuan.', 
      icon: <FileText className="w-6 h-6" />, 
      color: 'bg-rose-500',
      onDownload: handleDownloadFindings
    },
    { 
      title: 'Daftar Aset & Sertifikasi', 
      desc: 'Export seluruh database master data peralatan.', 
      icon: <CheckCircle2 className="w-6 h-6" />, 
      color: 'bg-indigo-500',
      onDownload: handleDownloadEquipments
    },
  ];

  return (
    <div className="space-y-8 animate-in fade-in duration-500">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Rekap & Laporan Audit</h1>
          <p className="text-slate-500 mt-1">Unduh laporan resmi untuk kebutuhan kepatuhan SMK3 dan ISO 45001.</p>
        </div>
        <div className="bg-emerald-50 text-emerald-700 px-4 py-2 rounded-xl text-xs font-bold border border-emerald-100 flex items-center gap-2">
            <Download className="w-4 h-4" /> Ready to Export
        </div>
      </div>

      {/* TODAY'S SUMMARY SECTION */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1 bg-white p-6 rounded-[2rem] border border-slate-100 shadow-sm flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-black text-slate-400 uppercase tracking-widest">Status Hari Ini</h3>
              <Calendar className="w-5 h-5 text-emerald-500" />
            </div>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-slate-600">Dijadwalkan</span>
                <span className="px-2.5 py-1 bg-blue-50 text-blue-600 text-xs font-black rounded-lg">{todayPlans.length}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-slate-600">Terlaksana</span>
                <span className="px-2.5 py-1 bg-emerald-50 text-emerald-600 text-xs font-black rounded-lg">{todayActuals.length}</span>
              </div>
              <div className="flex items-center justify-between border-t border-dashed border-slate-100 pt-4">
                <span className="text-sm font-bold text-rose-600 uppercase tracking-tight">Pending / Missed</span>
                <span className="px-2.5 py-1 bg-rose-50 text-rose-600 text-xs font-black rounded-lg">{pendingPlans.length}</span>
              </div>
            </div>
          </div>
          {pendingPlans.length > 0 && (
            <div className="mt-6 p-4 bg-rose-50 rounded-2xl border border-rose-100 flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-rose-500 shrink-0" />
              <p className="text-[10px] font-bold text-rose-800 leading-tight">
                Ada {pendingPlans.length} jadwal inspeksi yang terlewat dan memerlukan tindak lanjut segera.
              </p>
            </div>
          )}
        </div>

        <div className="lg:col-span-2 bg-slate-50 p-1 rounded-[2rem] border border-slate-200">
          <div className="bg-white p-6 rounded-[1.8rem] h-full shadow-sm">
            <h3 className="text-sm font-black text-slate-400 uppercase tracking-widest mb-4 flex items-center gap-2">
              <Database className="w-5 h-5 text-blue-500" /> Checklist Operasional Hari Ini
            </h3>
            <div className="max-h-[300px] overflow-y-auto custom-scrollbar space-y-3 pr-2">
              {[...todayPlans, ...pendingPlans].length > 0 ? (
                (() => {
                  const items = [...todayPlans, ...pendingPlans];
                  // Filter out items already done
                  const remainingItems = items.filter(p => !actuals.some(a => a.planId === p.id || (a.equipmentId === p.equipmentId && a.actualDate === p.plannedDate)));
                  
                  if (remainingItems.length === 0) {
                    return (
                      <div className="py-10 text-center text-slate-400">
                        <p className="text-xs italic font-black uppercase tracking-widest text-emerald-500">Semua inspeksi hari ini telah selesai!</p>
                      </div>
                    );
                  }

                  return remainingItems.map((p) => {
                    const eq = equipments.find(e => e.id === p.equipmentId);
                    const isPending = p.plannedDate < todayStr;

                    return (
                      <div key={p.id} className={`p-5 rounded-3xl border transition-all ${isPending ? 'bg-rose-50/50 border-rose-100 hover:border-rose-300' : 'bg-white border-slate-100 hover:border-emerald-300'} flex items-center justify-between group shadow-sm hover:shadow-md`}>
                        <div className="flex items-center gap-5">
                          <div className={`w-10 h-10 rounded-2xl flex items-center justify-center ${isPending ? 'bg-rose-100 text-rose-600' : 'bg-emerald-100 text-emerald-600'}`}>
                            {p.plannedDate === todayStr ? <Clock className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
                          </div>
                          <div>
                            <p className="text-sm font-black text-slate-900 tracking-tight">{eq?.code || 'N/A'}</p>
                            <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">
                              {eq?.type} • {eq?.area}
                            </p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-6">
                          <div className="text-right">
                             <p className={`text-[10px] font-black uppercase tracking-widest ${isPending ? 'text-rose-600' : 'text-blue-600'}`}>
                                {isPending ? `Pending (${p.plannedDate})` : 'Jadwal Hari Ini'}
                             </p>
                             <p className="text-[9px] text-slate-400 font-bold uppercase tracking-tighter">Wajib Cek</p>
                          </div>
                          <button 
                            onClick={() => goToInput?.(p.equipmentId)}
                            className="bg-emerald-600 text-white px-5 py-3 rounded-2xl text-[10px] font-black uppercase tracking-widest flex items-center gap-2 hover:bg-slate-950 transition-all shadow-xl shadow-emerald-500/20 active:scale-95"
                          >
                            Isi Sekarang <ArrowRight className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    );
                  });
                })()
              ) : (
                <div className="py-10 text-center text-slate-400">
                  <p className="text-xs italic font-medium">Tidak ada jadwal inspeksi yang aktif.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {reportTypes.map((report, i) => (
            <div key={i} className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm hover:shadow-md transition-all group flex gap-6 items-start">
                <div className={`p-4 ${report.color} text-white rounded-2xl shadow-lg shadow-current/20 group-hover:scale-110 transition-transform`}>
                    {report.icon}
                </div>
                <div className="flex-1">
                    <h3 className="text-lg font-bold text-slate-800">{report.title}</h3>
                    <p className="text-sm text-slate-500 mt-1 mb-6 h-10">{report.desc}</p>
                    <div className="flex gap-2">
                        <button 
                          onClick={report.onDownload}
                          className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-slate-900 text-white rounded-xl text-xs font-bold hover:bg-slate-800 transition-all active:scale-95"
                        >
                            <FileSpreadsheet className="w-4 h-4" /> Download Excel
                        </button>
                    </div>
                </div>
            </div>
        ))}
      </div>

      <div className="bg-slate-900 p-10 rounded-[2.5rem] text-white relative overflow-hidden shadow-2xl shadow-slate-900/20">
        <div className="relative z-10">
            <h2 className="text-3xl font-black mb-2">Custom Query Report</h2>
            <p className="text-slate-400 max-w-lg mb-10 text-lg leading-relaxed">
                Filter data spesifik untuk kebutuhan audit internal atau investigasi insiden secara cepat.
            </p>
            <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1 bg-white/10 backdrop-blur-md rounded-2xl p-4 flex items-center gap-4 border border-white/10">
                    <Search className="w-6 h-6 text-slate-400" />
                    <input 
                      type="text" 
                      value={customQuery}
                      onChange={(e) => setCustomQuery(e.target.value)}
                      placeholder="Cari berdasarkan Area, PIC, atau ID..." 
                      className="bg-transparent border-none focus:ring-0 text-base w-full placeholder:text-slate-500 outline-none text-white" 
                    />
                </div>
                <button 
                  onClick={handleDownloadCustom}
                  className="px-10 py-4 bg-emerald-500 hover:bg-emerald-600 text-white rounded-2xl font-black shadow-xl shadow-emerald-500/20 transition-all flex items-center gap-2 justify-center"
                >
                    <FileDown className="w-6 h-6" /> GENERATE
                </button>
            </div>
        </div>
        <div className="absolute -top-24 -right-24 p-8 opacity-[0.03] pointer-events-none rotate-12">
            <FileText className="w-96 h-96" />
        </div>
      </div>

      <div className="bg-amber-50 border border-amber-100 p-6 rounded-3xl flex items-start gap-4">
          <div className="p-3 bg-white rounded-2xl shadow-sm text-amber-500">
              <Database className="w-6 h-6" />
          </div>
          <div>
              <h4 className="font-bold text-amber-900">Catatan Audit</h4>
              <p className="text-sm text-amber-700 mt-1 leading-relaxed">
                  Semua laporan yang diunduh mencakup timestamp pembuatan dan metadata integritas data. Gunakan file Excel ini sebagai bukti lampiran dalam audit ISO 45001 atau SMK3 perusahaan Anda.
              </p>
          </div>
      </div>
    </div>
  );
};

export default Reports;
